/**
 * useHoldCommand.ts · 8-state hold matrix wired to POST /api/hold
 *
 * State machine (design.md §10, hallmark §7):
 *   idle          → operator hasn't touched the panel yet
 *   arming        → first click; 3s window to confirm-cancel
 *   armed         → second click; ready to fire (auto if `autoArm=true`)
 *   sending       → POST in flight
 *   sent-ack      → 2xx `accepted` response received
 *   ack-timeout   → no response in 5s (server hang, gateway down)
 *   error         → 4xx / 5xx / `rejected` payload
 *   disabled      → external gate (no active alert)
 *
 * Auto-transitions kept from Phase 3:
 *   arming → idle after 3s
 *   sent-ack → idle after 2.4s
 * New in Phase 4:
 *   sending → ack-timeout after 5s if response is still pending
 *
 * `execute()` performs the actual POST. The caller passes the alert
 * primitives (vehicle_id, stop_id, hold_seconds) — the hook does not
 * carry alert state itself.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { ApiError, isAbortError, postHold } from "../lib/api";
const initial = {
    state: "idle",
    lastResponse: null,
    lastError: null,
    history: [],
};
function reducer(prev, action) {
    switch (action.type) {
        case "advance": {
            // idle → arming → armed → sending (sending only from armed)
            if (prev.state === "idle")
                return { ...prev, state: "arming" };
            if (prev.state === "arming")
                return { ...prev, state: "armed" };
            if (prev.state === "armed")
                return { ...prev, state: "sending", lastError: null };
            if (prev.state === "ack-timeout")
                return { ...prev, state: "sending", lastError: null };
            if (prev.state === "error")
                return { ...prev, state: "sending", lastError: null };
            return prev;
        }
        case "sending":
            return { ...prev, state: "sending", lastError: null };
        case "ack": {
            const rolled = [action.response, ...prev.history].slice(0, 3);
            return {
                ...prev,
                state: "sent-ack",
                lastError: null,
                lastResponse: action.response,
                history: rolled,
            };
        }
        case "error":
            return {
                ...prev,
                state: "error",
                lastError: action.message,
                lastResponse: action.response ?? prev.lastResponse,
            };
        case "timeout":
            return { ...prev, state: "ack-timeout", lastError: "No ack in 5s." };
        case "reset":
            return { ...prev, state: "idle", lastError: null };
        case "force":
            return { ...prev, state: action.state };
        default:
            return prev;
    }
}
// ------------------------------------------------------------------
// Hook
// ------------------------------------------------------------------
export function useHoldCommand(options = {}) {
    const { ackTimeoutMs = 5_000, ackHoldMs = 2_400, armingMs = 3_000, } = options;
    const [state, dispatch] = useReducer(reducer, initial);
    const timeoutRef = useRef(null);
    const ackHoldRef = useRef(null);
    const armingRef = useRef(null);
    const abortRef = useRef(null);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (timeoutRef.current !== null)
                window.clearTimeout(timeoutRef.current);
            if (ackHoldRef.current !== null)
                window.clearTimeout(ackHoldRef.current);
            if (armingRef.current !== null)
                window.clearTimeout(armingRef.current);
            abortRef.current?.abort();
        };
    }, []);
    // Arming → idle after N ms (undo-window).
    useEffect(() => {
        if (state.state !== "arming")
            return;
        if (armingRef.current !== null)
            window.clearTimeout(armingRef.current);
        armingRef.current = window.setTimeout(() => {
            dispatch({ type: "reset" });
        }, armingMs);
        return () => {
            if (armingRef.current !== null) {
                window.clearTimeout(armingRef.current);
                armingRef.current = null;
            }
        };
    }, [state.state, armingMs]);
    // sent-ack → idle after N ms (settled frame).
    useEffect(() => {
        if (state.state !== "sent-ack")
            return;
        if (ackHoldRef.current !== null)
            window.clearTimeout(ackHoldRef.current);
        ackHoldRef.current = window.setTimeout(() => {
            dispatch({ type: "reset" });
        }, ackHoldMs);
        return () => {
            if (ackHoldRef.current !== null) {
                window.clearTimeout(ackHoldRef.current);
                ackHoldRef.current = null;
            }
        };
    }, [state.state, ackHoldMs]);
    const execute = useCallback(async (req) => {
        // Cancel any in-flight prior POST.
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        dispatch({ type: "sending" });
        // Arm the ack-timeout — if the POST resolves inside this window we
        // clear it; if not, we transition to `ack-timeout` regardless.
        if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = window.setTimeout(() => {
            // Only trip if we're still `sending` — successful acks reset first.
            dispatch({ type: "timeout" });
            ac.abort();
        }, ackTimeoutMs);
        try {
            const res = await postHold(req, { signal: ac.signal });
            if (!mountedRef.current)
                return null;
            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (res.status === "accepted") {
                dispatch({ type: "ack", response: res });
            }
            else {
                dispatch({
                    type: "error",
                    message: res.reject_reason ?? "Rejected by fleet gateway",
                    response: res,
                });
            }
            return res;
        }
        catch (err) {
            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (isAbortError(err)) {
                // Aborted by the timeout — the timeout dispatch already ran.
                return null;
            }
            if (err instanceof ApiError) {
                const bodyMessage = tryReadBodyMessage(err.body);
                dispatch({
                    type: "error",
                    message: `${err.short}${bodyMessage ? ` · ${bodyMessage}` : ""}`,
                });
            }
            else {
                dispatch({
                    type: "error",
                    message: err instanceof Error ? err.message : String(err),
                });
            }
            return null;
        }
    }, [ackTimeoutMs]);
    const advance = useCallback(() => dispatch({ type: "advance" }), []);
    const reset = useCallback(() => {
        abortRef.current?.abort();
        if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        dispatch({ type: "reset" });
    }, []);
    const force = useCallback((s) => dispatch({ type: "force", state: s }), []);
    return useMemo(() => ({
        state: state.state,
        advance,
        execute,
        reset,
        force,
        lastResponse: state.lastResponse,
        lastError: state.lastError,
        history: state.history,
    }), [state, advance, execute, reset, force]);
}
/** Pluck a human-readable message out of a Pydantic-style error body. */
function tryReadBodyMessage(body) {
    if (!body || typeof body !== "object")
        return null;
    const o = body;
    if (typeof o.detail === "string")
        return o.detail;
    if (typeof o.message === "string")
        return o.message;
    if (Array.isArray(o.detail) && o.detail.length > 0) {
        const first = o.detail[0];
        if (typeof first?.msg === "string")
            return first.msg;
    }
    return null;
}
